/**
 * CreatorIdeasTab
 *
 * Ideas list for the Creator view. A WorkspacePanel with the status
 * filter in the subheader slot (DD-007, PanelFilterTabs) and a single
 * flat column of IdeaCards. Shows all DRAFT + PENDING + APPROVED ideas
 * for the authenticated client (derived from the portal JWT). Clicking
 * an idea opens the creator idea drawer through the portal http client.
 */

import React, { useMemo, useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import { useIdeasQuery } from "@/domains/Ideas/hooks/useIdeas";
import { IdeaCard } from "@/domains/Ideas/components/IdeaCard";
import { Idea } from "@/domains/Ideas/types";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { CreatorIdeaDetailModal } from "./CreatorIdeaDetailModal";

type FilterId = "ALL" | "DRAFT" | "PENDING" | "APPROVED" | "READY";

interface CreatorIdeasTabProps {
  clientId: string;
}

export const CreatorIdeasTab: React.FC<CreatorIdeasTabProps> = ({ clientId }) => {
  const { t } = useLocale();
  const [filter, setFilter] = useState<FilterId>("ALL");
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  // Server-side filter: ALL returns DRAFT+PENDING+APPROVED+READY, otherwise
  // a single status.
  const serverStatus =
    filter === "ALL" ? "DRAFT,PENDING,APPROVED,READY" : filter;
  const { data: ideas = [], isLoading, error } = useIdeasQuery(
    clientId,
    serverStatus,
    false,
    true
  );

  const filterOptions = useMemo(
    () => [
      { id: "ALL", label: t("creator.ideas.filter.all") },
      { id: "DRAFT", label: t("creator.ideas.filter.draft") },
      { id: "PENDING", label: t("creator.ideas.filter.pending") },
      { id: "APPROVED", label: t("creator.ideas.filter.approved") },
      { id: "READY", label: t("creator.ideas.filter.ready") },
    ],
    [t]
  );

  const handleEdit = (idea: Idea) => setSelectedIdeaId(idea.id);
  const IdeaIcon = ENTITY_CONFIG.idea.icon;

  return (
    <>
      <WorkspacePanel
        title={t("creator.tabs.ideas")}
        icon={<IdeaIcon className="w-4 h-4" />}
        summary={ideas.length > 0 ? ideas.length : undefined}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        loading={isLoading}
        subheader={
          <PanelFilterTabs
            options={filterOptions}
            activeId={filter}
            onChange={(id) => setFilter(id as FilterId)}
          />
        }
      >
        {error ? (
          <div className="py-3 text-sm text-red-600">
            {t("creator.ideas.loadError")}
          </div>
        ) : ideas.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">
            {t("creator.ideas.empty")}
          </p>
        ) : (
          <ul className="space-y-3">
            {ideas.map((idea) => (
              <li key={idea.id}>
                <IdeaCard
                  idea={idea}
                  viewMode="creator"
                  onEdit={handleEdit}
                />
              </li>
            ))}
          </ul>
        )}
      </WorkspacePanel>

      <CreatorIdeaDetailModal
        ideaId={selectedIdeaId}
        clientId={clientId}
        isOpen={!!selectedIdeaId}
        onClose={() => setSelectedIdeaId(null)}
      />
    </>
  );
};
